/* eslint-disable react/no-danger */
import 'plyr-react/plyr.css';

import cn from 'classnames';
import {
  memo, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Link, useSearchParams } from 'react-router-dom';

import HeaderCatalog from '../../components/HeaderCatalog';
import MainPageFooter from '../../components/MainPageFooter';
import PreviewCard from '../../components/PreviewCard';
import RecommendModal from '../../components/RecommendModal';
import ResponseModal from '../../components/ResponseModal';
import ScrollToTop from '../../components/ScrollToTop';
import ArrowRightIcon from '../../components/SVGIcons/ArrowRightIcon';
import Loader from '../../components/SVGIcons/Loader';
import { Container } from '../../components/UI/Container';
import VacancySummary from '../../components/VacancySummary';
import VideoPlayer from '../../components/VideoPlayer';
import {
  requestDocuments,
  requestVacancy,
  resetVacancy,
} from '../../redux/actions/vacancy-page';
import { RootState } from '../../redux/reducers/rootReducer';
import { clearFromHTML } from '../../utils/clear-html';
import { SEO } from '../../utils/constants/seo';
import { filterContent } from '../../utils/filters/vacancyContentFilter';
import mediaQueries from '../../utils/media-query/media-query-constants';
import useMediaQuery from '../../utils/media-query/media-query-hook';
import { IVacancyFullInfo } from '../../utils/types/state-types';
import { useDocumentSeo } from '../../utils/useDocumentSeo';
import Page404 from '../Page404';
import { getLinkFromDivisionName } from './divisionLinkEnum';
import classes from './vacancy-page.module.scss';

function createMarkup(markup: any) {
  return { __html: markup };
}

function VacancyPage() {
  const isMobile = useMediaQuery(mediaQueries.mobile);
  const [searchParams] = useSearchParams();

  const vacancy = useSelector<RootState, IVacancyFullInfo | null>(
    (state) => state.vacancyReducer.vacancy,
  );
  const isLoading = useSelector<RootState, boolean>(
    (state) => state.vacancyReducer.isLoading,
  );
  const vacancyError = useSelector<RootState, string | undefined>(
    (state) => state.vacancyReducer.error,
  );

  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
  const [isRecommendRoleModalOpen, setIsRecommendRoleModalOpen] = useState(false);

  const dispatch = useDispatch();

  const {
    title, division, status, archivedAt,
  } = vacancy || {};

  const isArchived = useMemo(() => status?.value === 'AR', [status]);
  const [hidden, setHidden] = useState(isArchived);
  const [mainContent, sideContent] = filterContent(vacancy);
  const d = String(division?.value);
  const { vacancyIdForUrl, vacancyFriendlyUrl } = useParams();

  useDocumentSeo({
    title: vacancy?.title
      ? `${vacancy.title.value || 'Купер'}${SEO.vacancy.title}`
      : null,
    description: vacancy?.title
      ? `${vacancy?.title.value || 'Купер'}${SEO.vacancy.description}`
      : null,
  });

  useEffect(() => {
    if ((vacancyIdForUrl || vacancyFriendlyUrl) && searchParams.get('open')) {
      setIsResponseModalOpen(true);
    }
    dispatch(requestVacancy({ vacancyFriendlyUrl, vacancyIdForUrl }));
    dispatch(requestDocuments());
    return () => {
      dispatch(resetVacancy());
    };
  }, [vacancyIdForUrl, vacancyFriendlyUrl, searchParams, dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setHidden(isArchived);
  }, [isArchived]);

  const handleHidden = () => {
    if (!hidden) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
    setHidden((prev) => !prev);
  };

  return (
    <>
      {vacancyError && <Page404 />}
      {!vacancyError && (
        <div className={classes.vacancyPageContainer}>
          <ResponseModal
            titleResponse="Расскажите о себе"
            vacancy={vacancy && vacancy.id}
            division={d}
            recommendation={searchParams.get('recommendation')}
            closeModal={() => setIsResponseModalOpen(false)}
            isOpen={isResponseModalOpen}
            setIsOpen={setIsResponseModalOpen}
            defaultValues={{
              birthdate: '',
              currentWorkplace: '',
              currentJob: '',
              firstName: '',
              lastName: '',
              patronymic: '',
              expectedSalary: '',
              telegram: '',
              phoneNumber: '',
              email: '',
              coverLetter: '',
              cvFile: null as File | null,
              checkPersonalData: false,
              checkIT: false,
            }}
          />

          <RecommendModal
            isOpen={isRecommendRoleModalOpen}
            setIsOpen={setIsRecommendRoleModalOpen}
            vacancy={vacancy && vacancy.id}
            title={clearFromHTML(String(title?.value))}
            onCloseModal={() => setIsRecommendRoleModalOpen(false)}
            defaultValues={{
              recommenderFirstName: '',
              recommenderLastName: '',
              recommenderPatronymic: '',
              cvFile: null,
              recommenderPhoneNumber: '',
              recommenderEmail: '',
              checkPersonalData: false,
              checkIT: false,
              recommendedFirstName: '',
              recommendedLastName: '',
              recommendedPatronymic: '',
              recommendedPhoneNumber: '',
              recommendedEmail: '',
              concentToBeRecommended: false,
            }}
          />
          <HeaderCatalog
            className={classes['catalog-vacancies__header']}
            colored
            isSticked
          />

          <div className={classes['header-space']} />
          {!isResponseModalOpen
            && !isRecommendRoleModalOpen && <ScrollToTop showPosition={820} />}
          {isLoading && (
            <div className={classes.loader}>
              <Loader />
            </div>
          )}
          {!isLoading && (
            <>
              <div
                className={cn(classes['hero-block'], {
                  [classes.bgArchived]: isArchived,
                })}
              >
                <Container className={classes['hero-block__container']}>
                  <div className={classes['hero-block__title-wrapper']}>
                    <h1
                      className={cn(
                        classes['hero-block__title'],
                        classes[`hero-block__title--division-${d}`],
                        {
                          [classes['hero-block__title--archived']]: isArchived,
                        },
                      )}
                      dangerouslySetInnerHTML={createMarkup(title?.value)}
                    />
                  </div>
                </Container>
              </div>
              <div className={classes.mainArea}>
                <Container className={classes.VacancyPage__container}>
                  <div className={classes['VacancyPage__main-content-wrapper']}>
                    <div
                      className={cn(
                        classes['VacancyPage__main-content-hidden-wrapper'],
                        {
                          [classes[
                            'VacancyPage__main-content-hidden-wrapper--hidden'
                          ]]: hidden,
                        },
                      )}
                    >
                      <div className={classes['VacancyPage__main-content']}>
                        <div
                          className={
                            classes['VacancyPage__main-content-summary']
                          }
                        >
                          <VacancySummary
                            data={sideContent}
                            archivedAt={archivedAt}
                            isArchived={isArchived}
                          />
                          {!isMobile && !isArchived && (
                            <div
                              className={
                                classes[
                                  'VacancyPage__main-content-summary-buttons'
                                ]
                              }
                            >
                              <button
                                type="button"
                                className={cn(
                                  classes.button,
                                  classes.responseButton,
                                  classes[`responseButton${d}`],
                                )}
                                onClick={() => {
                                  setIsResponseModalOpen((prev) => !prev);
                                  window._mtm?.push({ event: 'buttonApply' });
                                }}
                              >
                                Откликнуться
                                <ArrowRightIcon fill="white" />
                              </button>
                              {vacancy?.isRecommendationAllowed && (
                                <button
                                  type="button"
                                  className={cn(
                                    classes.button,
                                    classes.recommendButton,
                                    classes[`recommendButton${d}`],
                                  )}
                                  onClick={() => setIsRecommendRoleModalOpen(true)}
                                >
                                  Реферальная программа
                                  <ArrowRightIcon fill="#1D1D1D" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        <div
                          className={cn(
                            classes['VacancyPage__main-content-body'],
                            classes.content,
                          )}
                        >
                          {mainContent
                            && mainContent.map(
                              ([, item]) => typeof item === 'object' && (
                              <div
                                className={classes.content__section}
                                key={item.displayName}
                                style={{ order: item.order }}
                              >
                                <div className={classes.content__title}>
                                  {item.displayName}
                                </div>
                                <div
                                  className={classes.content__body}
                                  dangerouslySetInnerHTML={createMarkup(
                                    item?.value,
                                  )}
                                />
                              </div>
                              ),
                            )}
                          {vacancy?.attachments
                            .filter((item) => item.type === 'video')
                            .map((item) => (
                              <div
                                key={item.id}
                                className={classes.content__section}
                              >
                                <VideoPlayer
                                  link={item.file}
                                  order={item.order}
                                />
                              </div>
                            ))}
                          {vacancy?.attachments
                            .filter((item) => item.type === 'image')
                            .map((item) => (
                              <div
                                key={item.id}
                                className={classes.content__section}
                              >
                                <img
                                  style={{
                                    maxWidth: 810,
                                    order: item.order,
                                  }}
                                  src={item.file}
                                  alt={item.file}
                                />
                              </div>
                            ))}
                          {vacancy?.news.map((item) => (
                            <PreviewCard
                              key={item.id}
                              picture={item.picture}
                              link={item.link}
                              text={item.description}
                              order={item.order}
                              division={d}
                            />
                          ))}
                        </div>
                        {isMobile && !isArchived && (
                          <div className={classes.extraButtons}>
                            <button
                              type="button"
                              className={cn({
                                [classes.button]: true,
                                [classes.responseButton]: true,
                                [classes.noSummary]: sideContent.length === 0,
                              })}
                              onClick={() => {
                                setIsResponseModalOpen((prev) => !prev);
                                window._mtm?.push({ event: 'buttonApply' });
                              }}
                            >
                              Откликнуться
                              <ArrowRightIcon fill="white" />
                            </button>
                            {vacancy?.isRecommendationAllowed && (
                              <button
                                type="button"
                                className={cn(
                                  classes.button,
                                  classes.recommendButton,
                                )}
                                onClick={() => setIsRecommendRoleModalOpen(true)}
                              >
                                Реферальная программа
                                {' '}
                                <ArrowRightIcon fill="#1D1D1D" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {hidden && (
                        <div
                          className={
                            classes[
                              'VacancyPage__main-content-hidden-wrapper-overlay'
                            ]
                          }
                        />
                      )}
                    </div>
                  </div>
                  {isArchived && (
                    <div
                      className={cn(
                        classes['VacancyPage__hidden-content-buttons'],
                        {
                          [classes[
                            'VacancyPage__hidden-content-buttons__open'
                          ]]: !hidden,
                        },
                      )}
                    >
                      <button
                        type="button"
                        onClick={handleHidden}
                        className={classes['VacancyPage__show-more-button']}
                      >
                        {hidden
                          ? 'Показать полное описание'
                          : 'Скрыть полное описание'}
                      </button>
                      <Link
                        to={getLinkFromDivisionName(division?.value)}
                        className={classes['VacancyPage__see-all-button']}
                      >
                        Актуальные вакансии
                        <ArrowRightIcon fill="white" />
                      </Link>
                    </div>
                  )}
                </Container>
              </div>
            </>
          )}
          <MainPageFooter isLoadingContent={isLoading} />
        </div>
      )}
    </>
  );
}

export default memo(VacancyPage);
